import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useDebouncedValue, useWindow } from '@krp/client-runtime'
import {
  COMMON_CATALOG_PAGE_SIZE,
  PLATFORM_WINDOWS,
  ProductPickerScreen,
  makeSelectCommonCatalogVm,
  peekScanFrame,
  pickPurposeOf,
  platformCatalogProducts,
  scanFrameUrl,
  type ProductPickerActions,
  type ProductPickerVm,
} from '@krp/platform-ui'

import { CONNECTION_FORMS } from '../../composition/connectionForms'
import { SEARCH_DEBOUNCE_MS } from './timings'
import { productPhotoUrl } from './useProductLinks'

const kindTitle = (kind: string) => CONNECTION_FORMS.titleOf(kind)

/** Как у сервера (`CatalogBrowse.MaxTextLength`): длиннее запрос не уходит. */
const MAX_QUERY = 128

/**
 * Binding окна «Выбор товара» (RP-FB56): режим «выбери товар» общего списка номенклатуры. Тот же запрос, что у «Общего каталога»
 * (единая служба текстового поиска) и та же модель строк; поле поиска предзаполнено прочитанным названием (`name` в адресе), человек
 * может его стереть. Выбор товара не пишет ничего: окно заменяется подтверждением «Для товара «…» установить это фото?»,
 * кадр (`frame` в адресе) остаётся на устройстве. Строка поиска и страница — память окна, в Redux их нет.
 */
export function ProductPickBinding() {
  const handle = useWindow()
  const [params] = useSearchParams()
  const purpose = pickPurposeOf(params.get('purpose'))
  const frameId = params.get('frame') ?? ''
  const readName = (params.get('name') ?? '').trim()
  const [query, setQuery] = useState(() => readName)
  const [page, setPage] = useState(1)
  const settled = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS)
  const args = useMemo(() => ({ query: settled, page, pageSize: COMMON_CATALOG_PAGE_SIZE }), [settled, page])
  const result = platformCatalogProducts.useQuery(args)
  const selectList = useMemo(() => makeSelectCommonCatalogVm(), [])
  const list = selectList({
    intent: { query, page },
    result: {
      ...(result.data !== undefined ? { data: result.data } : {}),
      ...(result.error !== undefined ? { error: result.error } : {}),
      isUninitialized: result.isUninitialized,
      isLoading: result.isFetching,
      isError: result.isError,
      isSuccess: result.isSuccess,
    },
    kindTitle,
  })
  const vm: ProductPickerVm = {
    purpose,
    list,
    framePreviewUrl: scanFrameUrl(frameId),
    frameLost: peekScanFrame(frameId) === undefined,
    readName: readName === '' ? null : readName,
  }

  const { close, replaceWith } = handle
  const refetch = result.refetch
  const actions: ProductPickerActions = {
    searchChanged: (text) => {
      setQuery(text.slice(0, MAX_QUERY))
      setPage(1)
    },
    pageChanged: (next) => setPage(Math.max(1, Math.trunc(next))),
    reload: () => void refetch(),
    // Окно подтверждения занимает место этого: возврат один, а кадр остаётся в адресе.
    pick: (row) => replaceWith(PLATFORM_WINDOWS.photoAttach, { productId: row.id }),
    close,
  }
  return <ProductPickerScreen vm={vm} actions={actions} photoUrl={productPhotoUrl} />
}
